import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  forkJoin,
  Observable,
  of,
  Subscription,
  throwError,
} from 'rxjs';
import {
  catchError,
  filter,
  map,
  publishReplay,
  refCount,
  shareReplay,
  switchMap,
} from 'rxjs/operators';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';
import TrimbleMaps, { Common } from '@trimblemaps/trimblemaps-js';
import {
  BatchLocationResponse,
  SingleSearchLocation,
  SingleSearchResponse,
} from '../_models/pcmws/singleSearch/singleSearchResponse';
import { MapService } from './map.service';
import { PoiCategoriesResponse } from '../_models/pcmws/singleSearch/poiCategoriesResponse';
import { singleSearchPOICategories } from '../utils/poiCategories';
import { UserService } from './user.service';
import { RegionCode } from '../utils/regions';
import { CustomEncoder } from '../auth/helpers/customEncoder';
import { SubscriptionLevel } from '../auth/models/user';
import {
  CountriesResponse,
  PoiTypesResponse,
  SearchTypesResponse,
  StatesResponse,
} from '../_models/pcmws/singleSearch/singleSearchTypesResponse';
import { AlertService } from '../auth/services/alert.service';

export interface ErrorObject {
  index: number;
  searchString?: string;
  message: string;
  status?: number;
}

export interface BulkReverseGeocodeResponses {
  responses: BatchLocationResponse[];
  error: ErrorObject;
}

@Injectable({
  providedIn: 'root',
})
export class SearchService {
  mapRegion: string;
  getPOICategories$: Observable<string>;
  counter = 0;

  constructor(
    private http: HttpClient,
    private mapService: MapService,
    private userService: UserService,
    private alertService: AlertService
  ) {
    this.getPOICategories$ = this.mapService.map$.pipe(
      filter((mapData) => mapData !== null),
      switchMap((mapData) => {
        return this.getPoiCategories(mapData.getRegion());
      }),
      publishReplay(),
      refCount()
    );
  }

  regionCheck(region: string) {
    this.mapRegion = region || Common.Region.NA;

    if (
      region !== Common.Region.NA &&
      region !== Common.Region.EU &&
      region !== Common.Region.WW
    ) {
      this.mapRegion = Common.Region.WW;
    }
  }

  removeNullValuesFromQueryParams(params: HttpParams) {
    const paramsKeysAux = params.keys();
    paramsKeysAux.forEach((key) => {
      const value = params.get(key);
      if (
        value === null ||
        value === 'undefined' ||
        value === '' ||
        value === 'null'
      ) {
        params['map'].delete(key);
      }
    });
    return params;
  }

  search(
    searchString: string,
    searchRegion: string,
    searchCenter?: { lng: number; lat: number }
  ): Observable<SingleSearchLocation[]> {
    return this.getPOICategories$.pipe(
      shareReplay(1),
      switchMap((poiCategories: string) => {
        if (!searchString.replace(/\s/g, '')) {
          return of(null);
        }
        this.regionCheck(searchRegion);
        let queryParam;
        const searchOption = this.userService.getSearchOptions();
        const poiTypes = searchOption?.poiTypes?.join(',');
        const countries = searchOption?.countries?.join(',');
        const states = searchOption?.states?.join(',');
        const excludeSearchTypes = searchOption?.excludeTypes?.some(
          (val) => val === 'None'
        )
          ? null
          : searchOption?.excludeTypes?.join(',');
        const includeSearchTypes = searchOption?.includeTypes?.some(
          (val) => val === 'None'
        )
          ? null
          : searchOption?.includeTypes?.join(',');
        const useCustomPlaces = searchOption?.useCustomPlaces;
        const useMapCenter = searchOption?.useMapCenter
          ? `${searchCenter.lng},${searchCenter.lat}`
          : null;
        const maxResults = searchOption?.noOfMaxResults;
        const radiusFromCurrentLonLat = searchOption?.radiusFromCurrentLonLat;
        if (
          this.userService.getUser().subscriptionLevel ===
          SubscriptionLevel.PREMIUM
        ) {
          queryParam = new HttpParams()
            .set('authToken', environment.singleSearchKey)
            .set('countryType', 'FIPS')
            .set('excludedSearchTypes', excludeSearchTypes)
            .set('maxResults', maxResults)
            .set('poiCategories', poiTypes)
            .set('countries', countries)
            .set('states', states)
            .set('query', searchString)
            .set('useCustomPlaces', useCustomPlaces)
            .set('currentLonLat', useMapCenter)
            .set('includeOnly', includeSearchTypes)
            .set('radiusFromCurrentLonLat', radiusFromCurrentLonLat);
        }
        if (
          this.userService.getUser().subscriptionLevel !==
          SubscriptionLevel.PREMIUM
        ) {
          queryParam = new HttpParams({ encoder: new CustomEncoder() })
            .set('authToken', environment.singleSearchKey)
            .set('countryType', 'FIPS')
            .set('excludedSearchTypes', excludeSearchTypes)
            .set('maxResults', maxResults)
            .set('query', searchString)
            .set('includeOnly', includeSearchTypes);
        }

        if (searchOption?.includePlaces) {
          queryParam = queryParam.delete('excludedSearchTypes');
          queryParam = queryParam.delete('poiCategories');
        }
        if (!searchOption?.useMapCenter) {
          queryParam = queryParam.delete('radiusFromCurrentLonLat');
        }
        queryParam = this.removeNullValuesFromQueryParams(queryParam);
        return this.http
          .get(
            `${environment.singleSearchbaseUrl}/${this.mapRegion}/api/search/`,
            {
              params: queryParam,
            }
          )
          .pipe(
            map((r: SingleSearchResponse) => {
              return r.Locations;
            })
          );
      })
    );
  }

  bulkReverseGeoCode(
    searchStrings: string[]
  ): Observable<(SingleSearchLocation | ErrorObject)[]> {
    return forkJoin(
      searchStrings.map((searchString: string) => {
        return this.getAddress(searchString).pipe(
          map((data) => {
            const index = searchStrings.findIndex(
              (val) => searchString === val
            );
            if (data?.Errors?.length) {
              data.id = index;
            }
            return data;
          }),
          catchError((err) => {
            const index = searchStrings.findIndex(
              (val) => searchString === val
            );
            const errorData: ErrorObject = {
              index: index,
              searchString: searchString,
              message: err?.error?.ErrString,
              status: err?.status,
            };
            return of(errorData);
          })
        );
      })
    );
  }

  batchAddressSearch(
    address: SingleSearchLocation[],
    updateddataVersion?: string
  ): Observable<BatchLocationResponse[]> {
    const dataVersion =
      this.userService.getUser().region !== 'NA'
        ? this.userService.getUser().dataset
        : updateddataVersion || 'Current';
    const url = `${environment.pcmwsUrl}/locations/address/batch`;
    // Using header to keep the key out of the URL
    const headers = new HttpHeaders({
      authorization: environment.apiKey,
      batchIndex: (this.counter++).toString(),
    });

    const params = {
      dataset: dataVersion,
    };

    return this.http
      .post<BatchLocationResponse[]>(
        url,
        { Locations: address },
        { headers, params }
      )
      .pipe(
        map((sr) => sr),
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  getBatchResults(locations): Observable<any> {
    const totalImports = 1;
    return this.batchAddressSearch(locations).pipe(
      map((response) => response),
      catchError((error) => {
        return error;
      })
    );
  }

  getAddress(searchString: string): Observable<SingleSearchLocation> {
    // ? Do we need this to be region based?
    const url = `${environment.pcmwsUrl}/locations`;

    // Using header to keep the key out of the URL
    const headers = new HttpHeaders({
      Authorization: environment.singleSearchKey,
    });

    const params = {
      coords: searchString,
      region: RegionCode[this.userService.getUser().region],
      dataset:
        this.userService.getUser().dataset ||
        this.userService.getUser().dataVersion ||
        'Current',
    };

    return this.http.get<BatchLocationResponse>(url, { headers, params }).pipe(
      map((sr) => sr[0]),
      (error) => error
    );
  }

  reverseGeocode(
    lngLat: TrimbleMaps.LngLatLike
  ): Observable<SingleSearchLocation> {
    const latLong = TrimbleMaps.LngLat.convert(lngLat);
    const currentUser = this.userService.getUser();
    // ? Do we need this to be region based?
    const url = `${environment.singleSearchbaseUrl}/${currentUser.region}/api/search/`;

    // Using header to keep the key out of the URL
    const headers = new HttpHeaders({
      Authorization: environment.singleSearchKey,
    });
    let params;
    if (
      this.userService.getUser().subscriptionLevel === SubscriptionLevel.PREMIUM
    ) {
      params = new HttpParams({ encoder: new CustomEncoder() })
        .set('query', [latLong.lat, latLong.lng].toString())
        .set('matchNamedRoadsOnly', true)
        .set('maxResults', 1);
    }
    if (
      this.userService.getUser().subscriptionLevel !== SubscriptionLevel.PREMIUM
    ) {
      params = new HttpParams({ encoder: new CustomEncoder() })
        .set('query', [latLong.lat, latLong.lng].toString())
        .set('maxResults', 1)
        .set('includeOnly', 'city,state,zip');
    }
    // const params = {
    //   query: [latLong.lat, latLong.lng].toString(),
    //   matchNamedRoadsOnly: true,
    //   maxResults: 1,
    // };

    return this.http.get<SingleSearchResponse>(url, { headers, params }).pipe(
      map((sr) => sr.Locations[0]),
      (error) => error
    );
  }

  getPoiCategories(region): Observable<string> {
    const queryParam = new HttpParams().set(
      'authToken',
      environment.singleSearchKey
    );
    this.regionCheck(region);
    return this.http
      .get<PoiCategoriesResponse>(
        `${environment.singleSearchbaseUrl}/${this.mapRegion}/api/search/poiCategories/`,
        {
          params: queryParam,
        }
      )
      .pipe(
        map((categories: PoiCategoriesResponse) =>
          categories?.POICategories.filter((item) =>
            singleSearchPOICategories.includes(item)
          )
            .map((item) => item.replace(/\s/g, '+'))
            ?.join(',')
        )
      );
  }

  setsearchOptions(
    options: [
      PoiTypesResponse,
      SearchTypesResponse,
      CountriesResponse,
      StatesResponse
    ]
  ) {
    options = options || [null, null, null, null];
    const user = this.userService.getUser();

    if (localStorage) {
      localStorage.setItem('searchOptionsStore', JSON.stringify(options));
    }
  }

  hasSearchOptions(): boolean {
    let searchOptions = localStorage
      ? JSON.parse(localStorage.getItem('searchOptionsStore'))
      : {};
    searchOptions = searchOptions || null;
    return searchOptions === null ? false : true;
  }

  getSearchOptions(): [
    PoiTypesResponse,
    SearchTypesResponse,
    CountriesResponse,
    StatesResponse
  ] {
    let searchOptions = localStorage
      ? JSON.parse(localStorage.getItem('searchOptionsStore'))
      : {};
    searchOptions = searchOptions || null;
    return searchOptions;
  }

  retrieveSingleSearchOptionDataSubscription(): Subscription {
    return this.singleSearchOptions().subscribe(
      (
        allSingleSearchOptions: [
          PoiTypesResponse,
          SearchTypesResponse,
          CountriesResponse,
          StatesResponse
        ]
      ) => {
        this.setsearchOptions(allSingleSearchOptions);
      },
      (err) => {
        this.setsearchOptions(null);
        const errorMessage =
          'There was an error retrieving the Single Search Options, please try again later. Make sure you have permissions to access the feature.'; // Todo-yz Temporary message.
        this.alertService.error(errorMessage, true, 'app-modal-alert', true);
      }
    );
  }

  singleSearchOptions() {
    // this.regionCheck(this.userService?.getUser()?.region);
    this.mapRegion = this.userService?.getUser()?.region;
    const queryParam = new HttpParams().set(
      'authToken',
      environment.singleSearchKey
    );
    const poiTypes = `${environment.singleSearchbaseUrl}/${this.mapRegion}/api/search/poiTypes/`;
    const types = `${environment.singleSearchbaseUrl}/${this.mapRegion}/api/search/types/`;
    const countries = `${environment.singleSearchbaseUrl}/${this.mapRegion}/api/search/countries/`;
    const states = `${environment.singleSearchbaseUrl}/${this.mapRegion}/api/search/states/`;
    // this.regionCheck(region);
    return forkJoin([
      this.http.get<PoiTypesResponse>(poiTypes, {
        params: queryParam,
      }),
      this.http.get<SearchTypesResponse>(types, {
        params: queryParam,
      }),
      this.http.get<CountriesResponse>(countries, {
        params: queryParam,
      }),
      this.http.get<StatesResponse>(states, {
        params: queryParam,
      }),
    ]);
  }
}
